import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Globe, Plus, Trash2, ShieldAlert, LocateFixed, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function NetworkAccessTab() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ cidr: "", label: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/allowed-ips").then((r) => setData(r.data)).catch((e) => console.error("allow-list", e)), []);
  useEffect(() => { load(); }, [load]);

  const add = async (cidr, label) => {
    if (!cidr.trim()) return toast.error("Enter an IP address or range");
    setBusy(true);
    try { await api.post("/admin/allowed-ips", { cidr, label }); toast.success(`${cidr.trim()} added`); setForm({ cidr: "", label: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); } finally { setBusy(false); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Remove ${e.cidr} from the allow-list?`)) return;
    try { await api.delete(`/admin/allowed-ips/${e.id}`); toast.success(`${e.cidr} removed`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove"); }
  };

  if (!data) return <div className="h-40 border border-border rounded-sm animate-pulse bg-secondary/40" data-testid="network-loading" />;
  return (
    <div data-testid="network-access">
      <div className="flex flex-wrap items-start gap-4 mb-5">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 text-sm font-semibold"><Globe className="h-4 w-4 text-primary" /> Trusted office &amp; VPN addresses</div>
          <p className="text-xs text-muted-foreground mt-1">Only these IPs / CIDR ranges can reach the portal. Changes take effect within a minute. The config-file list was imported once; this page is now the source of truth.</p>
        </div>
        <div className="border border-border rounded-sm px-3 py-2 text-xs" data-testid="network-client-ip">
          <span className="text-muted-foreground">You're connecting from</span> <span className="font-mono font-semibold">{data.client_ip || "unknown"}</span>
          {data.client_allowed
            ? <span className="ml-2 inline-flex items-center gap-1 text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" />allowed</span>
            : data.client_ip && <Button size="sm" variant="outline" className="ml-2 h-7" data-testid="network-add-my-ip" onClick={() => add(data.client_ip, "Added from Admin")}><LocateFixed className="h-3.5 w-3.5 mr-1" />Add my IP</Button>}
        </div>
      </div>

      {data.open_to_all && (
        <div className="flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 text-amber-800 rounded-sm px-3 py-2 text-sm mb-4" data-testid="network-open-warning">
          <ShieldAlert className="h-4 w-4" /> The allow-list is empty — the portal is currently reachable from any network. Add your office IP now.
        </div>
      )}
      {!data.open_to_all && !data.client_allowed && data.client_ip && (
        <div className="flex items-center gap-2 border border-red-500/40 bg-red-500/10 text-red-700 rounded-sm px-3 py-2 text-sm mb-4" data-testid="network-lockout-warning">
          <ShieldAlert className="h-4 w-4" /> Your current IP ({data.client_ip}) is not on the list — you're only in via an internal route. Add it before you leave this page.
        </div>
      )}

      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end border border-border bg-card rounded-sm p-4 mb-5" data-testid="network-add-form">
        <div><Label className="text-xs">IP address or CIDR range</Label><Input data-testid="network-cidr" placeholder="203.0.113.7 or 203.0.113.0/29" value={form.cidr} onChange={(e) => setForm({ ...form, cidr: e.target.value })} onKeyDown={(e) => e.key === "Enter" && add(form.cidr, form.label)} /></div>
        <div><Label className="text-xs">Label</Label><Input data-testid="network-label" placeholder="e.g. Atlanta office, Ken home VPN" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
        <Button data-testid="network-add-btn" disabled={busy} onClick={() => add(form.cidr, form.label)}><Plus className="h-4 w-4 mr-1.5" />Allow</Button>
      </div>

      <div className="border border-border rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60"><TableHead>Address / range</TableHead><TableHead>Label</TableHead><TableHead>Source</TableHead><TableHead>Added</TableHead><TableHead className="text-right">Remove</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {data.entries.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8" data-testid="network-empty">No entries.</TableCell></TableRow>
            ) : data.entries.map((e) => (
              <TableRow key={e.id} data-testid={`network-row-${e.id}`} className={e.covers_you ? "bg-emerald-500/5" : ""}>
                <TableCell className="font-mono text-sm">{e.cidr}{e.covers_you && <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">you</span>}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{e.label || "—"}</TableCell>
                <TableCell><span className={`text-[11px] px-1.5 py-0.5 rounded-sm font-semibold ${e.source === "config" ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"}`}>{e.source === "config" ? "Config import" : "Admin"}</span></TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmt(e.created_at)}{e.added_by && e.added_by !== "system" ? ` · ${e.added_by}` : ""}</TableCell>
                <TableCell className="text-right"><button onClick={() => remove(e)} className="text-muted-foreground hover:text-destructive" title="Remove" data-testid={`network-remove-${e.id}`}><Trash2 className="h-4 w-4" /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
